/**
 * Navigation und äußeres Gerüst der App.
 *
 * Am Handy erscheint unten eine Leiste, ab Tablet eine Seitenleiste. Die
 * Navigation kommt von React Router; die Einträge sind an einer Stelle
 * festgelegt, damit Seitenleiste und untere Leiste gleich aussehen.
 */
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { AppIcon } from './components/AppIcon.tsx';
import { AppFooter } from './components/AppFooter.tsx';
import { SyncBanner } from './components/SyncBanner.tsx';
import type { IconName } from './components/AppIcon.tsx';
import { texts } from './texts/de.ts';

/** Ein Bereich der App. */
interface NavItem {
  readonly path: string;
  readonly label: string;
  readonly icon: IconName;
  /** Erscheint in der unteren Leiste am Handy. */
  readonly primary: boolean;
}

/** Bereiche der App. */
export const navItems: readonly NavItem[] = [
  { path: '/rezepte', label: texts.nav.recipes, icon: 'book', primary: true },
  { path: '/merkliste', label: texts.nav.favorites, icon: 'heart', primary: true },
  { path: '/suche', label: texts.nav.search, icon: 'search', primary: true },
  { path: '/einkauf', label: texts.nav.shopping, icon: 'cart', primary: true },
  { path: '/produkte', label: texts.nav.products, icon: 'tag', primary: false },
  { path: '/kochbuecher', label: texts.nav.cookbooks, icon: 'library', primary: false },
  { path: '/einstellungen', label: texts.nav.settings, icon: 'sliders', primary: true },
];

/** Prüft, ob ein Bereich aktiv ist, auch bei Unterseiten. */
function isActive(pathname: string, path: string): boolean {
  return pathname === path || pathname.startsWith(`${path}/`);
}

/** Seitenleiste für Tablet und Desktop. */
function SideNav({ pathname }: { readonly pathname: string }) {
  return (
    <nav
      aria-label="Hauptbereiche"
      className="hidden w-60 shrink-0 border-r border-border-subtle bg-surface-sunken p-4 md:block print:hidden"
    >
      <div className="mb-6 flex items-center gap-2 px-2">
        <AppIcon name="hat" size={28} className="text-accent" />
        <span className="text-lg font-semibold">{texts.app.name}</span>
      </div>
      <ul className="space-y-1">
        {navItems.map((item) => {
          const active = isActive(pathname, item.path);
          return (
            <li key={item.path}>
              <NavLink
                to={item.path}
                aria-current={active ? 'page' : undefined}
                className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors ${
                  active
                    ? 'bg-accent-soft font-semibold text-accent-strong'
                    : 'text-content-muted hover:bg-surface-raised hover:text-content'
                }`}
              >
                <AppIcon name={item.icon} size={20} />
                {item.label}
              </NavLink>
            </li>
          );
        })}
      </ul>
      <div className="mt-6 px-2">
        <NavLink
          to="/rezepte/neu"
          className="flex items-center justify-center gap-2 rounded-xl bg-accent px-3 py-2.5 text-sm font-semibold text-on-accent"
        >
          <AppIcon name="plus" size={18} />
          {texts.nav.newRecipe}
        </NavLink>
      </div>
    </nav>
  );
}

/** Untere Leiste für das Handy. */
function BottomNav({ pathname }: { readonly pathname: string }) {
  return (
    <nav
      aria-label="Hauptbereiche"
      className="fixed inset-x-0 bottom-0 z-20 flex border-t border-border-subtle bg-surface-raised pb-[env(safe-area-inset-bottom)] md:hidden print:hidden"
    >
      {navItems
        .filter((item) => item.primary)
        .map((item) => {
          const active = isActive(pathname, item.path);
          return (
            <NavLink
              key={item.path}
              to={item.path}
              aria-current={active ? 'page' : undefined}
              className={`flex flex-1 flex-col items-center gap-1 py-2.5 text-[11px] transition-colors ${
                active ? 'font-semibold text-accent' : 'text-content-muted'
              }`}
            >
              <AppIcon name={item.icon} size={22} />
              {item.label}
            </NavLink>
          );
        })}
    </nav>
  );
}

/** Gerüst mit Kopfzeile, Navigation und Inhaltsbereich. */
export function AppLayout() {
  const { pathname } = useLocation();

  return (
    <div className="flex min-h-full">
      <SideNav pathname={pathname} />
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b border-border-subtle bg-surface/95 px-4 py-3 backdrop-blur md:hidden print:hidden">
          <div className="flex items-center gap-2">
            <AppIcon name="hat" size={24} className="text-accent" />
            <span className="font-semibold">{texts.app.name}</span>
          </div>
          <NavLink to="/rezepte/neu" aria-label={texts.nav.newRecipe} className="text-accent">
            <AppIcon name="plus" size={24} />
          </NavLink>
        </header>
        <main className="flex flex-1 flex-col pb-20 md:pb-0 print:pb-0">
          <div className="print:hidden">
            <SyncBanner />
          </div>
          {/* Inhalt füllt den Platz, damit die Fußzeile auch auf kurzen Seiten unten steht. */}
          <div className="flex-1">
            <Outlet />
          </div>
          <AppFooter />
        </main>
      </div>
      <BottomNav pathname={pathname} />
    </div>
  );
}
